"use client";

import { Link } from "@/i18n/routing";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import * as React from "react";
import { UseCase } from ".";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "../ui/navigation-menu";
import { navigationConfig } from "@/config/navigation";

const ListItem = React.forwardRef<React.ElementRef<typeof Link>, React.ComponentPropsWithoutRef<typeof Link>>(
  ({ className, title, children, ...props }, ref) => {
    return (
      <li>
        <NavigationMenuLink asChild>
          <Link
            ref={ref}
            className={cn(
              "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
              className,
            )}
            {...props}
          >
            <div dangerouslySetInnerHTML={{ __html: title! }} className="text-sm font-medium leading-none" />
            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">{children}</p>
          </Link>
        </NavigationMenuLink>
      </li>
    );
  },
);
ListItem.displayName = "ListItem";

const NavigationComponent = ({ useCases }: { useCases: UseCase[] }) => {
  const t = useTranslations();

  return (
    <nav className="hidden items-center space-x-6 lg:flex">
      <NavigationMenu>
        <NavigationMenuList>
          {/* Pricing (Proxies) Menu */}
          <NavigationMenuItem>
            <NavigationMenuTrigger>{t(navigationConfig.proxiesGroup.titleKey)}</NavigationMenuTrigger>
            <NavigationMenuContent>
              <ul className="grid gap-3 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
                {/* Featured Item */}
                <li className="row-span-3">
                  <NavigationMenuLink asChild>
                    <Link
                      href={navigationConfig.proxiesGroup.featured!.href}
                      className="flex h-full w-full select-none flex-col justify-end rounded-md bg-gradient-to-b from-muted/50 to-muted p-6 no-underline outline-none focus:shadow-md"
                    >
                      <div className="mb-2 mt-4 text-lg font-medium">
                        {t(navigationConfig.proxiesGroup.featured!.titleKey)}
                      </div>
                      <p className="text-sm leading-tight text-muted-foreground">
                        {t(navigationConfig.proxiesGroup.featured!.descriptionKey)}
                      </p>
                    </Link>
                  </NavigationMenuLink>
                </li>

                {/* Proxy Types */}
                {navigationConfig.proxiesGroup.items.map((item) => (
                  <ListItem key={item.key} href={item.href} title={t(item.titleKey)}>
                    {t(item.descriptionKey)}
                  </ListItem>
                ))}
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>

          {/* Use Cases Menu */}
          <NavigationMenuItem>
            <NavigationMenuTrigger>{t("header.navigation.items.use_cases.title")}</NavigationMenuTrigger>
            <NavigationMenuContent>
              <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                {useCases.map((component) => (
                  <ListItem key={component.title} title={component.title} href={component.link}>
                    <span dangerouslySetInnerHTML={{ __html: component.description! }} />
                  </ListItem>
                ))}
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>

          {/* Flat Links */}
          {navigationConfig.main
            .filter((item) => item.type !== "group" && item.type !== "dynamic")
            .map((item) => (
              <NavigationMenuItem key={item.key}>
                <Link href={item.href!} legacyBehavior passHref>
                  <NavigationMenuLink className={navigationMenuTriggerStyle()}>{t(item.titleKey)}</NavigationMenuLink>
                </Link>
              </NavigationMenuItem>
            ))}

          <NavigationMenuItem>
            <NavigationMenuTrigger>{t(navigationConfig.freeToolsGroup.titleKey)}</NavigationMenuTrigger>
            <NavigationMenuContent>
              <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                {/* Proxy Types */}
                {navigationConfig.freeToolsGroup.items.map((item) => (
                  <ListItem key={item.key} href={item.href} title={t(item.titleKey)}>
                    {t(item.descriptionKey)}
                  </ListItem>
                ))}
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>
    </nav>
  );
};

export default NavigationComponent;
